import { logDebug, logWarn } from '@ptek/client-logger';
import { AuthClientConfig, TokenStorageType } from '@ptek/shared-auth-sdk';
import { HostSeamlessAuthConfig } from '../types/widget';
import { purgeHostTokenCache } from '../utils/authCache';

let hostSeamlessAuthConfig: HostSeamlessAuthConfig | null = null;

/**
 * Set the host seamless auth configuration
 * This is called by the widget constructor when hostSeamlessAuth is provided
 */
export const setHostSeamlessAuthConfig = (
  config: HostSeamlessAuthConfig | null
): void => {
  hostSeamlessAuthConfig = config;
  purgeHostTokenCache();
};

const getHostSeamlessAuthConfig = (): HostSeamlessAuthConfig | null => {
  return hostSeamlessAuthConfig;
};

const isHostSeamlessAuthEnabled = (): boolean => {
  const envEnabled = import.meta.env.VITE_AUTH_REQUIRED === 'true';
  const configAvailable = getHostSeamlessAuthConfig() !== null;
  return envEnabled && configAvailable;
};

/**
 * Check if logout should be hidden
 * Idea is if seamless auth is enabled with no other auth option, we don't want to show the logout button
 * However if cognito fallback is enabled, we are allowing the legacy login logout, and the button should be shown
 */
export const shouldHideLogout = (): boolean => {
  if (!isHostSeamlessAuthEnabled()) {
    return false;
  }
  const config = getHostSeamlessAuthConfig();
  if (!config) {
    return false;
  }
  return config.allowFallbackToCognito === false;
};

/**
 * Get the auth configuration
 * This is a getter function that evaluates the config when accessed
 */
export const getAuthConfig = (): AuthClientConfig => {
  return {
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
    region: import.meta.env.VITE_COGNITO_REGION,
    tokenStorage:
      (import.meta.env.VITE_AUTH_TOKEN_STORAGE as TokenStorageType) ||
      'localStorage',
    autoRefresh: true,
    refreshThreshold: 300,
    debugLogging: import.meta.env.VITE_AUTH_DEBUG_LOGGING === 'true',

    // SDK callback is required; AuthProvider handles auth state changes.
    onAuthStateChange: (
      isAuthenticated: boolean,
      reason?: 'login' | 'logout' | 'token_revoked' | 'session_expired'
    ) => {
      logDebug('AuthConfig', 'Widget auth state change:', {
        isAuthenticated,
        reason,
        authMode: isHostSeamlessAuthEnabled() ? 'hostSeamless' : 'cognito',
      });
    },

    hostSeamlessAuth: isHostSeamlessAuthEnabled()
      ? {
          getTokenCallback: getHostSeamlessAuthConfig()!.getTokenCallback,
          allowFallbackToCognito:
            getHostSeamlessAuthConfig()!.allowFallbackToCognito ?? true,
          onTokenError:
            getHostSeamlessAuthConfig()!.onTokenError ||
            ((error) => {
              if (import.meta.env.VITE_AUTH_DEBUG_LOGGING === 'true') {
                logWarn(
                  'AuthConfig',
                  'Host seamless auth error, falling back to Cognito:',
                  error
                );
              }
            }),
        }
      : undefined,

    // Token introspection for live auth control
    introspection: {
      enabled: import.meta.env.VITE_AUTH_INTROSPECTION_ENABLED !== 'false',
      interval: parseInt(
        import.meta.env.VITE_AUTH_INTROSPECTION_INTERVAL || '30'
      ),
      onActivity:
        import.meta.env.VITE_AUTH_INTROSPECTION_ON_ACTIVITY !== 'false',
      onVisibilityChange:
        import.meta.env.VITE_AUTH_INTROSPECTION_ON_VISIBILITY !== 'false',
      onApiCall: import.meta.env.VITE_AUTH_INTROSPECTION_ON_API_CALL === 'true',
    },
  };
};
